const RECEIVED_STATUSES = new Set([
  'material received',
  'payment done',
  'payment received',
  'completed'
]);

const CLOSED_REQUIREMENT_STATUSES = new Set([
  'cancelled',
  'rejected',
  'closed',
  'accepted',
  'ordered',
  'quoted'
]);

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeStatus(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function uniqueNames(names) {
  return [...new Set(names.filter(Boolean))];
}

function positiveId(...candidates) {
  for (const value of candidates) {
    const id = Number(value);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return null;
}

function rfqRequirementId(rfq) {
  return positiveId(rfq?.vendor_requirement_id, rfq?.requirement_id, rfq?.vendorRequirement?.id);
}

function orderRfqId(order) {
  return positiveId(order?.quotation?.rfq_id, order?.quotation?.rfq?.id, order?.rfq_id);
}

function requirementAlreadyQuoted(requirement) {
  return (
    asList(requirement?.quotationRequest).length > 0 ||
    asList(requirement?.rfq).length > 0
  );
}

export function requirementItemNames(requirement) {
  return uniqueNames(
    asList(requirement?.vendorRequirmentMappingItems).map(
      (mapping) => mapping?.items?.item_name || mapping?.item?.item_name
    )
  );
}

export function rfqItemNames(rfq) {
  return uniqueNames(asList(rfq?.rfqItems).map((row) => row?.item?.item_name || row?.category?.name));
}

export function orderItemNames(order) {
  return uniqueNames(asList(order?.poItems).map((row) => row?.item?.item_name));
}

function titleFromNames(names, fallback) {
  if (!names.length) return fallback;
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

export function mapRequirementCard(requirement) {
  const names = requirementItemNames(requirement);
  return {
    id: `req-${requirement.id}`,
    entityId: requirement.id,
    kind: 'requirement',
    title: titleFromNames(names, requirement?.task?.name || `Requirement #${requirement.id}`),
    subtitle: requirement?.project?.name || '—',
    meta: requirement?.user?.name ? `Raised by ${requirement.user.name}` : requirement?.task?.name || '',
    status: requirement?.status || 'Pending',
    href: `/builder/inventory/requirements/${requirement.id}`
  };
}

export function mapRfqCard(rfq) {
  const names = rfqItemNames(rfq);
  const vendorCount = asList(rfq?.vendors).length;
  return {
    id: `rfq-${rfq.id}`,
    entityId: rfq.id,
    kind: 'rfq',
    title: titleFromNames(names, `RFQ #${rfq.id}`),
    subtitle: rfq?.project?.name || '—',
    meta: vendorCount ? `${vendorCount} vendor${vendorCount === 1 ? '' : 's'}` : 'No vendors yet',
    status: rfq?.status || 'pending',
    href: `/builder/inventory/orders/requirement/${rfq.id}`
  };
}

export function mapOrderCard(order) {
  const names = orderItemNames(order);
  const amount =
    order?.total_amount != null
      ? `₹ ${Number(order.total_amount).toLocaleString('en-IN')}`
      : '';
  return {
    id: `po-${order.id}`,
    entityId: order.id,
    kind: 'order',
    title: titleFromNames(names, `PO #${order.id}`),
    subtitle: order?.project?.name || '—',
    meta: [order?.vendor?.user?.name, amount].filter(Boolean).join(' · '),
    status: order?.status || 'Pending',
    href: `/builder/inventory/orders/items/${order.id}`
  };
}

export function mapStockCard(item) {
  const criticality = Number(item?.stock_criticality || 0);
  return {
    id: `stock-${item.id}`,
    entityId: item.id,
    kind: 'stock',
    title: item?.item_name || `SKU #${item.id}`,
    subtitle: item?.category?.name || item?.category_name || 'Stock',
    meta: `Avail ${item?.availableStock ?? 0} · Criticality ${criticality}%`,
    status: criticality <= 100 ? 'Critical' : 'Low',
    href: '/builder/inventory/stock'
  };
}

export function buildInventoryPipeline({
  requirements = [],
  rfqs = [],
  orders = [],
  stock = []
} = {}) {
  const rfqByRequirementId = new Set(
    asList(rfqs)
      .map(rfqRequirementId)
      .filter(Boolean)
  );
  const orderedRfqIds = new Set(asList(orders).map(orderRfqId).filter(Boolean));

  const requested = asList(requirements)
    .filter((requirement) => {
      const status = normalizeStatus(requirement?.status || 'Pending');
      if (CLOSED_REQUIREMENT_STATUSES.has(status)) return false;
      if (requirementAlreadyQuoted(requirement)) return false;
      return !rfqByRequirementId.has(Number(requirement.id));
    })
    .map(mapRequirementCard);

  const quoted = asList(rfqs)
    .filter((rfq) => {
      if (normalizeStatus(rfq?.status) === 'expired') return false;
      const rfqId = positiveId(rfq?.id);
      return !rfqId || !orderedRfqIds.has(rfqId);
    })
    .map(mapRfqCard);

  const ordered = [];
  const received = [];
  asList(orders).forEach((order) => {
    const card = mapOrderCard(order);
    if (RECEIVED_STATUSES.has(normalizeStatus(order?.status))) {
      received.push(card);
    } else {
      ordered.push(card);
    }
  });

  const lowStock = asList(stock)
    .filter((item) => {
      const criticality = Number(item?.stock_criticality || 0);
      return criticality > 0 && criticality <= 125;
    })
    .sort((a, b) => Number(a.stock_criticality || 0) - Number(b.stock_criticality || 0))
    .slice(0, 12)
    .map(mapStockCard);

  return { requested, quoted, ordered, received, lowStock };
}

export const INVENTORY_PIPELINE_COLUMNS = [
  {
    id: 'requested',
    title: 'Requested',
    hint: 'Site raised, waiting for RFQ'
  },
  {
    id: 'quoted',
    title: 'Quoted',
    hint: 'RFQ with vendors'
  },
  {
    id: 'ordered',
    title: 'Ordered',
    hint: 'PO out, not received yet'
  },
  {
    id: 'received',
    title: 'Received',
    hint: 'Material on site'
  }
];
